import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Typography, Row, Col, Spin, Button } from 'antd';
import TopSellingMakesModelsCard from './components/TopSellingMakesModelsCard';
import RecommendedListingsCard from './components/RecommendedListingsCard';
import RecentlySearchedCard from './components/RecentlySearchedCard';
import LotPerformanceCard from './components/LotPerformanceCard';
import SubscriptionLockOverlay from './components/SubscriptionLockOverlay';
import { fetchRecentSearchedCars, fetchRecentRecommendedListings, fetchPopularCars } from "./services/api";
import './components/SubscriptionLockOverlay.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const { Title } = Typography;

/** Normalize user details from API (handles both { subscription_status } and { data: { subscription_status } }). */
function normalizeUserDetails(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const details = raw.data ?? raw;
  return typeof details === 'object' && details !== null ? details : null;
}

/** True when subscription is expired or explicitly inactive (show lock overlay). */
function isSubscriptionExpired(userDetails) {
  const details = normalizeUserDetails(userDetails);
  if (!details) return false;
  const status = details.subscription_status;
  if (status === 'expired' || status === 'EXPIRED') return true;
  if (status === 'inactive' || status === 'INACTIVE') return true;
  if (details.isActiveSubscription === false) return true;
  return false;
}

/**
 * Map API response item to Recently Searched card shape: { name, vin, active, sold, dom, mileage, version }
 * API shape: { year?, make?, model?, version?, trim?, vin, mileage, sold_dom, active_count, sold_count, ... }
 * Title = year make model trim (e.g. "2021 Toyota Camry SE")
 */
function mapRecentSearchedItem(item) {
  if (!item || typeof item !== "object") return null;
  const titleParts = [item.year, item.make, item.model, item.trim || item.version].filter(Boolean);
  const name = titleParts.length > 0 ? titleParts.join(" ") : item.vin || "Vehicle";
  const vin = item.vin || "";
  const active = item.active_count ?? 0;
  const sold = item.sold_count ?? 0;
  const dom = item.sold_dom ?? 0;
  const mileage = item.mileage;
  const version = item.version || item.trim;
  const profitVal = item.profit ?? item.estimated_profit;
  const profit = profitVal != null && typeof profitVal === "number"
    ? `$${Math.round(profitVal).toLocaleString()}`
    : profitVal != null ? `$${profitVal}` : null;
  return { name, vin, active, sold, dom, mileage, version, profit };
}

/**
 * Map API listing to RecommendedListingsCard shape.
 * API: { heading, asking_price, miles, units_active, sold_count, sold_dom, profit, vdp_url, photo }
 */
function mapRecommendedListing(item, index) {
  if (!item || typeof item !== "object") return null;
  const title = item.heading || item.title || "Vehicle";
  const price = item.asking_price ?? item.price;
  const priceStr =
    typeof price === "number" ? `$${Math.round(price).toLocaleString()}` : (price ? `$${price}` : "—");
  const mileageVal = item.miles ?? item.mileage;
  const mileageStr =
    mileageVal != null ? Number(mileageVal).toLocaleString() : "—";
  const unitsActive = item.units_active ?? 0;
  const soldDom = item.sold_dom ?? item.dom ?? 0;
  const unitsSold = item.sold_count ?? 0;
  const soldCount = item.sold_count;
  const profitVal = item.profit;
  const profitStr =
    profitVal != null && typeof profitVal === "number"
      ? `$${Math.round(profitVal).toLocaleString()}`
      : profitVal != null
        ? `$${profitVal}`
        : null;
  const profitNum = typeof profitVal === "number" ? profitVal : null;
  const grade = item.grade ?? (profitNum != null && profitNum >= 5000 ? "A" : profitNum != null && profitNum >= 2000 ? "B" : "C");
  const demand = item.demand ?? (grade === "A" ? "Strong" : "Steady");
  const supply = item.supply ?? item.units_active ?? unitsActive;
  return {
    id: item.vdp_url || `listing-${index}`,
    img: item.photo || item.img || item.image || item.image_url || "",
    title,
    mainPrice: priceStr,
    mileage: mileageStr,
    unitsActive,
    unitsSold,
    soldDom,
    rankBadge:
      soldCount != null ? { num: String(soldCount), label: "Index" } : null,
    profit: profitStr,
    vdp_url: item.vdp_url,
    grade: typeof grade === "string" ? grade.toUpperCase().charAt(0) : "B",
    demand: demand === "Strong" ? "Strong" : "Steady",
    supply,
  };
}

const DashboardPage = () => {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [recentSearched, setRecentSearched] = useState([]);
  const [recommendedListings, setRecommendedListings] = useState([]);
  const [popularCars, setPopularCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userDetails, setUserDetails] = useState(null);

  const subscriptionLocked = isSubscriptionExpired(userDetails);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const [searchedRes, listingsRes, popularCarsRes] = await Promise.all([
        fetchRecentSearchedCars(),
        fetchRecentRecommendedListings(),
        fetchPopularCars().catch((err) => {
          console.warn("Failed to load popular cars:", err);
          return { popular_cars: [] };
        }),
      ]);
      const searchedList = searchedRes?.recent_searched_cars ?? searchedRes?.data?.recent_searched_cars ?? (Array.isArray(searchedRes) ? searchedRes : []);
      const searchedItems = Array.isArray(searchedList) ? searchedList : [];
      setRecentSearched(searchedItems.map(mapRecentSearchedItem).filter(Boolean));

      const listingsList = listingsRes?.listings ?? listingsRes?.recent_recommended_listings ?? listingsRes?.data?.listings ?? (Array.isArray(listingsRes) ? listingsRes : []);
      const listingsItems = Array.isArray(listingsList) ? listingsList : [];
      setRecommendedListings(listingsItems.map((item, i) => mapRecommendedListing(item, i)).filter(Boolean));

      const popularList = popularCarsRes?.popular_cars ?? popularCarsRes?.data?.popular_cars ?? (Array.isArray(popularCarsRes) ? popularCarsRes : []);
      setPopularCars(Array.isArray(popularList) ? popularList : []);
    } catch (err) {
      console.error("❌ Failed to load dashboard:", err);
      setError("We couldn't load the dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  // Fetch user details with Clerk token so subscription status is available (no race with localStorage)
  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setUserDetails(null);
      return;
    }
    let cancelled = false;
    getToken({ template: 'default' })
      .then((token) => {
        if (cancelled || !token) return null;
        return fetch(`${BASE_URL}/user/details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      })
      .then((res) => {
        if (cancelled || !res || !res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setUserDetails(data);
      })
      .catch(() => {
        if (!cancelled) setUserDetails(null);
      });
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, getToken]);

  return (
    <div className="dashboard-content" style={{ padding: '0 24px 12px', paddingLeft: '24px', position: 'relative', paddingTop: '24px', marginTop: '-60px', overflow: 'visible' }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '100px' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", marginTop: 100 }}>
          <Title level={4} type="danger">🚫 {error}</Title>
          <Button type="primary" style={{ marginTop: 16 }} onClick={loadDashboard}>
            Try again
          </Button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Grid wrapper: when subscription expired, blur + opacity + no pointer events */}
          <div className={subscriptionLocked ? 'subscription-lock-content' : ''}>
            {/* Home Title */}
            <div className="dashboard-home-title-row" style={{ marginTop: '0px', marginBottom: '12px', paddingTop: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <Title
                level={1}
                style={{
                  color: '#ffffff',
                  marginBottom: 0,
                  fontWeight: 700,
                  fontSize: '20px',
                  marginTop: 0,
                  marginLeft: '0',
                  paddingTop: 0,
                  lineHeight: 1.3,
                  overflow: 'visible'
                }}
              >
                Home
              </Title>
            </div>

            {/* Two Column Layout */}
            <Row gutter={[16, 0]}>
              <Col xs={24} lg={16}>
                <div style={{ marginBottom: '12px' }}>
                  <TopSellingMakesModelsCard
                  data={popularCars}
                  loading={loading}
                  city={userDetails?.dealership_city ?? userDetails?.data?.dealership_city}
                  state={userDetails?.dealership_state ?? userDetails?.data?.dealership_state}
                />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <RecommendedListingsCard listings={recommendedListings} />
                </div>
              </Col>
              <Col xs={24} lg={8} style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '12px', flex: '0 0 auto' }}>
                  <RecentlySearchedCard searches={recentSearched} />
                </div>
                <div style={{ flex: '0 0 auto' }}>
                  <LotPerformanceCard />
                </div>
              </Col>
            </Row>
          </div>

          {subscriptionLocked && <SubscriptionLockOverlay />}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
